<script setup lang="ts">
import { computed, ref } from 'vue'
import { userModule } from '@/store/user/module'
import UIKit from '@pocketprep/ui-kit'

interface ICancellationSection {
    id: 'examResult' | 'cancelReason'
    label: string
    options: { value: string; label: string }[]
}

const {
    SidePanel,
    Button: PocketButton,
    Textarea: PocketTextarea,
} = UIKit

const { isLoading } = defineProps<{
    isLoading: boolean
}>()

const emit = defineEmits<{
    'close': []
    'submitCancel': [ feedback: string | null ]
    'selectionsUpdated': [{ [sectionId: string]: null | string }]
}>()

const sections: ICancellationSection[] = [
    {
        id: 'examResult',
        label: 'Did you take your exam?',
        options: [
            { value: 'passed', label: 'I passed' },
            { value: 'failed', label: 'I didn\'t pass' },
            { value: 'not_taken', label: 'I didn\'t take it' },
        ],
    },
    {
        id: 'cancelReason',
        label: 'What is your primary reason for canceling?',
        options: [
            { value: 'no_need_after_renewal', label: 'I shouldn\'t need it past my renewal date' },
            { value: 'no_longer_studying', label: 'I\'m no longer studying for the exam' },
            { value: 'plan_to_upgrade_later', label: 'I plan to upgrade again later' },
            { value: 'not_getting_value', label: 'I wasn\'t getting the value I expected' },
            { value: 'using_other_tool', label: 'I\'m using another tool, app, or resource' },
        ],
    },
]
const choiceHoverId = ref<string | null>(null)
const selections = ref<{ [sectionId: string]: null | string }>({})
const feedback = ref<null | string>(null)

const isDarkMode = computed(() => {
    return userModule.state.settings.isDarkMode
})

const updateSelections = (selectionId: string, choiceId: string) => {
    if (selections.value[selectionId] !== choiceId) {
        selections.value[selectionId] = choiceId
        emit('selectionsUpdated', selections.value)
    }
}

</script>

<template>
    <Portal to="sidepanel">
        <SidePanel
            key="cancelSubscriptionSidepanel"
            class="cancel-subscription-sidepanel"
            name="Cancel Subscription"
            width="medium"
            :is-dark-mode="isDarkMode"
            @close="emit('close')"
        >
            <div v-for="section in sections"
                 :key="section.id"
                 class="cancel-subscription-sidepanel__section"
            >
                <h3
                    class="cancel-subscription-sidepanel__section-head"
                    tabindex="0"
                >
                    {{ section.label }}
                </h3>
                <div
                    v-for="choice in section.options"
                    v-dark
                    tabindex="0"
                    :key="choice.value"
                    @mouseenter="choiceHoverId = choice.value"
                    @mouseleave="choiceHoverId = null"
                    class="cancel-subscription-sidepanel__choice"
                    :class="{ 'cancel-subscription-sidepanel__choice--selected':
                                  selections[section.id] === choice.value,
                              'cancel-subscription-sidepanel__choice--hover':
                                  choice.value === choiceHoverId }"
                    @click.stop="updateSelections(section.id, choice.value)"
                    @keydown.enter.stop="updateSelections(section.id, choice.value)"
                >
                    {{ choice.label }}
                </div>
            </div>
            <div class="cancel-subscription-sidepanel__feedback-input-label">
                If none of these seem right, or you'd like to provide other feedback:
            </div>
            <PocketTextarea
                v-model="feedback"
                class="cancel-subscription-sidepanel__feedback-input"
                :is-dark-mode="isDarkMode"
                height="62"
                width="100%"
            />
            <template #action>
                <PocketButton
                    :is-dark-mode="isDarkMode"
                    :is-loading="isLoading"
                    @click="emit('submitCancel', feedback)"
                >
                    Cancel Subscription
                </PocketButton>
            </template>
        </SidePanel>
    </Portal>
</template>

<style lang="scss" scoped>
.cancel-subscription-sidepanel {
    :deep(.uikit-side-panel__content) {
        padding: 36px 33px;

        @include breakpoint(black-bear) {
            padding: 30px 21px 30px 16px;
        }
    }

    &__section {
        margin-bottom: 40px;
    }

    &__section-head {
        font-size: 17px;
        font-weight: 600;
        line-height: 25px;
        padding-left: 15px;
        margin-top: 0;
        margin-bottom: 20px;
    }

    &__choice {
        background-color: $white;
        border-radius: 5px;
        padding: 12px 46px 12px 15px;
        font-size: 16px;
        font-weight: 500;
        letter-spacing: -0.1px;
        line-height: 21px;
        position: relative;
        transition: 0.1s padding ease;

        &--dark {
            background-color: $brand-black;
            color: $gray-background;

            &::after {
                border-color: $slate;
                box-shadow: 0 1px 4px 0 rgba($jet, 0.3);
            }
        }

        &::after {
            content: '';
            position: absolute;
            top: -1px;
            bottom: -1px;
            left: -1px;
            right: -1px;
            border: 0.5px solid rgba($pewter, 0.85);
            box-shadow: 0 1px 4px 0 rgba($ash, 0.3);
            border-radius: 5px;
            pointer-events: none;
        }

        &--hover {
            cursor: pointer;

            &::after {
                display: block;
                top: -2px;
                bottom: -2px;
                left: -2px;
                right: -2px;
                border: 2px solid $slate;
                border-radius: 7px;
            }

            &--dark::after {
                border-color: rgba($white, 0.6);
            }
        }

        &--focus {
            &::after {
                display: block;
                border: 1px solid $brand-blue;
            }

            &--dark::after {
                border-color: $banana-bread;
            }
        }

        &--selected {
            &::after {
                display: block;
                border: 2px solid $brand-blue;
            }

            &--dark::after {
                border-color: $banana-bread;
            }

            &--dark:hover::after {
                border-color: $butterscotch;
            }

            &--dark:focus::before {
                border-color: $banana-bread;
            }
        }
    }

    &__choice:not(:last-child) {
        margin-bottom: 10px;
    }

    &__feedback-input-label {
        font-size: 17px;
        font-weight: 600;
        line-height: 25px;
        margin-left: 0;
        padding-left: 15px;
        margin-bottom: 20px;
    }

    :deep(.uikit-textarea__textarea) {
        padding: 11px 15px;
        letter-spacing: -0.1px;
    }
}
</style>
